import React, { createContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../../app/src/store/store';
import {
  setUser,
  clearUser,
  setLoading,
} from '../../../app/src/store/authSlice';
import {
  useProfile,
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useSocialLoginMutation,
  useRefreshProfile,
} from '../../utils/authQueries';
import { sendEmailSignInLink } from '../../utils/firebase';
import type { LoginCredentials, RegisterData, User } from '../../utils/types';
import { useToaster } from '../Toaster';
import { useAutoTokenRefresh } from '../../utils/useAutoTokenRefresh';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<boolean>;
  register: (data: RegisterData) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  socialLogin: (idToken: string) => Promise<boolean>;
  sendPasswordlessLink: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const { user, isLoading: storeLoading } = useSelector(
    (state: RootState) => state.auth
  );

  // Enable automatic token refresh
  useAutoTokenRefresh();

  // Fetch profile using React Query (only if token exists)
  const hasToken =
    typeof window !== 'undefined' && !!localStorage.getItem('accessToken');
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile(hasToken);

  // Mutations
  const loginMutation = useLoginMutation();
  const registerMutation = useRegisterMutation();
  const logoutMutation = useLogoutMutation();
  const socialLoginMutation = useSocialLoginMutation();
  const refreshProfile = useRefreshProfile();

  // Sync profile data to Redux store
  useEffect(() => {
    if (profileData) {
      dispatch(setUser(profileData));
    } else if (profileError && !profileLoading) {
      // Only clear user if there's an auth error
      const error = profileError as { message?: string };
      if (
        error.message?.includes('unauthorized') ||
        error.message?.includes('invalid')
      ) {
        dispatch(clearUser());
      }
    }
  }, [profileData, profileError, profileLoading, dispatch]);

  // Update loading state
  useEffect(() => {
    dispatch(setLoading(profileLoading));
  }, [profileLoading, dispatch]);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      dispatch(setLoading(true));
      const data = await loginMutation.mutateAsync(credentials);
      dispatch(setUser(data.user));
      toaster.success('Login successful');
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message || 'Login failed';
      toaster.error(errorMessage);
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const register = async (data: RegisterData): Promise<boolean> => {
    try {
      dispatch(setLoading(true));
      const result = await registerMutation.mutateAsync(data);
      dispatch(setUser(result.user));
      toaster.success('Registration successful');
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Registration failed';
      toaster.error(errorMessage);
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await logoutMutation.mutateAsync();
      dispatch(clearUser());
      toaster.info('Logged out successfully');
    } catch {
      // Clear user state even if API call fails
      dispatch(clearUser());
      toaster.error('Logout failed, but you have been logged out locally');
    }
  };

  const refreshUser = async (): Promise<void> => {
    await refreshProfile();
  };

  const socialLogin = async (idToken: string): Promise<boolean> => {
    try {
      dispatch(setLoading(true));
      const data = await socialLoginMutation.mutateAsync(idToken);
      dispatch(setUser(data.user));
      toaster.success('Signed in successfully');
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : (err as { message?: string })?.message || 'Social login failed';
      toaster.error(errorMessage);
      return false;
    } finally {
      dispatch(setLoading(false));
    }
  };

  const sendPasswordlessLink = async (email: string): Promise<void> => {
    const redirectUrl =
      typeof window !== 'undefined'
        ? `${window.location.origin}/email-callback`
        : '';
    await sendEmailSignInLink(email, redirectUrl);
    toaster.success(`Magic link sent to ${email} — check your inbox!`);
  };

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading: storeLoading || profileLoading,
    login,
    register,
    logout,
    refreshUser,
    socialLogin,
    sendPasswordlessLink,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
